"use client";

import { ButtonField, FeatursCard } from "@/components/index";
import ContactForm from "@/app/features/contactUs/components/ContactForm";
import { Contacts } from "@/app/features/contactUs/data/cTAInfo/Contacts";
import { faArrowLeftLong } from "@/components/icons/Index";
import { useRouter } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";

const BUTTONS = [{ key: "partnerLogin", target: "/#b2b" }] as const;

const CONTACT_KEYS = ["email", "phone", "address", "hours"] as const;

export default function ContactUs() {
  const router = useRouter();
  const locale = useLocale();
  const t = useTranslations("ContactUs");

  const dir = locale === "fa" ? "rtl" : "ltr";

  const handleNavigation = (target: string) => {
    const [path, hash] = target.split("#");

    if (path === "" || path === "/") {
      if (window.location.pathname === "/" && hash) {
        document.getElementById(hash)?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      } else if (target === "/") {
        router.push("/");
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        router.push(target);
      }

      return;
    }

    router.push(target);
  };

  // Translate contact labels and values while preserving their icons.
  const localizedContacts = Contacts.map((contact, index) => {
    const key = CONTACT_KEYS[index];

    return {
      ...contact,
      label: t(`contacts.${key}.label`),
      ...(key === "address" || key === "hours"
        ? { value: t(`contacts.${key}.value`) }
        : {}),
    };
  });

  return (
    <main lang={locale} dir={dir} className="min-h-screen overflow-hidden">
      {/* Hero Section */}
      <section
        id="top"
        className="relative isolate px-4 pb-16 pt-24 sm:px-6 sm:pb-20 sm:pt-28 lg:px-8 lg:pb-24 lg:pt-40"
      >
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_70%_20%,rgba(216,179,106,.18),transparent_35%),radial-gradient(circle_at_20%_80%,rgba(75,85,99,.15),transparent_35%)]" />

        <div className="mx-auto w-full max-w-6xl">
          <h1
            className={`max-w-4xl bg-gradient-to-r from-yellow-700 via-yellow-100 to-yellow-200 bg-clip-text text-4xl font-bold leading-[.98] tracking-tight text-transparent sm:text-5xl md:text-7xl lg:text-8xl ${
              dir === "rtl" ? "text-right" : "text-left"
            }`}
          >
            {t("hero.title")}
            <br />
            <span className="text-white">{t("hero.highlight")}</span>
          </h1>

          <p className="mt-6 max-w-2xl text-base leading-7 text-white/55 sm:mt-8 sm:text-lg sm:leading-8">
            {t("hero.description")}
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <div className="relative">
        <img
          src="/images/Visa.webp"
          alt=""
          loading="lazy"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
        />

        <div className="relative">
          {/* Contact Features */}
          <section
            id="contact"
            className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8 lg:py-32"
          >
            <FeatursCard data={localizedContacts} />
          </section>

          {/* Contact Form */}
          <section
            id="contactForm"
            className="isolate mx-auto grid w-full max-w-6xl grid-cols-1 gap-8 px-4 py-16 sm:gap-10 sm:px-6 sm:py-24 lg:grid-cols-2 lg:gap-12 lg:px-8 lg:py-32"
          >
            <div>
              <p className="text-sm uppercase tracking-[.3em] text-[#d8b36a]">
                {t("form.eyebrow")}
              </p>

              <h2 className="mt-5 text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
                {t("form.title")}
                <br />
                {t("form.highlight")}
              </h2>

              <p className="mt-5 max-w-md text-sm leading-7 text-white/45 sm:mt-6 sm:text-base">
                {t("form.description")}
              </p>
            </div>

            <div className="min-w-0 rounded-2xl border glass-02 glass-border-illuminate p-4 sm:p-7">
              <ContactForm />
            </div>
          </section>

          {/* Call to Action */}
          <section
            id="cta"
            className="isolate mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8 lg:py-32"
          >
            <p className="mx-auto max-w-3xl text-center text-sm leading-7 text-white/60 sm:text-base sm:leading-8">
              {t("cta.description")}
            </p>

            <div className="mx-auto flex w-full max-w-[700px] flex-col items-stretch justify-center gap-4 text-center sm:mt-10 sm:flex-row sm:items-center">
              {BUTTONS.map(({ key, target }) => (
                <ButtonField
                  type="button"
                  key={key}
                  iconName={faArrowLeftLong}
                  label={t(`buttons.${key}`)}
                  btnSize="large"
                  className="w-full sm:w-auto"
                  color="inherit"
                  onClick={() => handleNavigation(target)}
                />
              ))}
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}
